import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CreditsService } from '../../../shared/services/credits.service';
import { Budget } from 'src/app/shared/models/credit-service-dtos';

/**
 * String formatted amounts for display in template.
 * Shows up to two truncated decimal places using language-specific separator, but omits the
 * separator and decimals entirely if the truncated fractional part is 00.
 * Minus sign is preserved for negative values (> 0 magnitude).
 */
export interface FormattedAmounts {
    total: string;
    inclusive: string;
    extra: string;
    used: string;
    blocked: string;
    available: string;
}

@Component({
    selector: 'app-credits',
    templateUrl: './credits.component.html',
    styleUrl: './credits.component.scss',
    standalone: false,
})
export class CreditsComponent implements OnInit {
    private readonly AMOUNT_FONT: string = '20px Manrope-Regular, sans-serif';
    private readonly LABEL_FONT: string = '16px Manrope-Medium, sans-serif';
    private readonly CONTAINER_HORIZONTAL_PADDING: number = 10;
    private readonly LABEL_AMOUNT_GAP: number = 20;
    private readonly INCLUSIVE_TRANSLATION_KEY: string =
        'user.userArea.credits.view.inclusive';
    private readonly EXTRA_TRANSLATION_KEY: string =
        'user.userArea.credits.view.extra';
    private readonly USED_TRANSLATION_KEY: string =
        'user.userArea.credits.view.used';
    private readonly BLOCKED_TRANSLATION_KEY: string =
        'user.userArea.credits.view.blocked';
    private readonly AVAILABLE_TRANSLATION_KEY: string =
        'user.userArea.credits.view.available';
    private readonly INFINITY_CHARACTER: string = '∞';

    @Input() userId: string;

    @ViewChild('creditsViewWrapper') creditsViewWrapper: ElementRef;

    @Output() changeSubscriptionClicked: EventEmitter<void> =
        new EventEmitter();

    formattedAmounts: FormattedAmounts = {
        total: '0',
        inclusive: '0',
        extra: '0',
        used: '0',
        blocked: '0',
        available: '0',
    };

    isUnlimited: boolean;

    //Budget details properties
    showInclusive: boolean = true;
    showInclusiveText: boolean = true;
    inclusiveWidth: number = 100;

    showExtra: boolean = false;
    showExtraText: boolean = true;
    extraWidth: number = 0;

    // Usage details properties
    usedWidth: number = 0;
    blockedWidth: number = 0;
    availableWidth: number = 100;

    showUsed: boolean = false;
    showBlocked: boolean = false;

    showUsedText: boolean = false;
    showBlockedText: boolean = false;
    showAvailableText: boolean = true;

    // Period end date properties
    periodEndDate: string;
    dateFormat: string;

    // Loading spinner flag
    showLoadingSpinner: boolean = true;

    constructor(
        private creditsService: CreditsService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.creditsService.getUserBudget(this.userId).subscribe({
            next: (budget: Budget) => {
                this.isUnlimited = budget.isUnlimited;
                this.periodEndDate = budget.periodEndDate;
                this.setDateFormatBasedOnLang();

                // Build formatted amounts directly from original budget using truncation logic.
                this.buildFormattedAmounts(budget);

                setTimeout(() => {
                    this.calculateWidths();
                    this.showLoadingSpinner = false;
                }, 0);
            },
            error: () => {
                this.showLoadingSpinner = false;
            },
        });
    }

    private setDateFormatBasedOnLang() {
        const currentLang = this.translateService.getCurrentLang();

        switch (currentLang) {
            case 'en':
                this.dateFormat = 'MMMM d, yyyy, HH.mm a';
                break;
            case 'de':
                this.dateFormat = 'dd.MM.yyyy, HH.mm';
                break;
        }
    }

    private buildFormattedAmounts(budget: Budget): void {
        const inclusive: string = this.truncateAndFormatAmount(
            budget.inclusive
        );
        const extra: string = this.truncateAndFormatAmount(budget.extra);
        const used: string = this.truncateAndFormatAmount(budget.used, false);
        const blocked: string = this.truncateAndFormatAmount(
            budget.blocked,
            false
        );
        let available: string;
        let total: string;

        if (budget.isUnlimited) {
            // Unlimited subscription -> show ∞
            available = '∞';
            total = '∞';
        } else {
            // Regular subscription: calculate as before
            total = this.truncateAndFormatAmount(
                this.parseFormatted(inclusive) + this.parseFormatted(extra)
            );
            available = this.truncateAndFormatAmount(
                this.parseFormatted(total) -
                    this.parseFormatted(used) -
                    this.parseFormatted(blocked)
            );
        }

        this.formattedAmounts = {
            inclusive,
            extra,
            used,
            blocked,
            available,
            total,
        };
    }

    /**
     * Truncates a numeric value to two decimal places and formats it for display.
     * The truncation behaviour floor or seal is determined by the isFloor parameter.
     *
     * This method performs floor/ceil-based truncation (not rounding) to ensure consistent
     * precision handling, then formats the result according to the current locale:
     * - English (en): uses dot as decimal separator (e.g., "123.45")
     * - German (de): uses comma as decimal separator (e.g., "123,45")
     *
     * The method omits the decimal part entirely if it equals "00" after truncation,
     * displaying only the integer part (e.g., 250.00 → "250").
     *
     * Handles edge cases:
     * - Very small values like -0.004 are truncated to "0" (no "-0")
     * - Negative values preserve the minus sign
     *
     * @param value - The numeric value to truncate and format
     * @returns Formatted string representation with appropriate decimal separator,
     *
     */
    private truncateAndFormatAmount(
        value: number,
        shouldRoundDown: boolean = true
    ): string {
        const isNegative = value < 0;
        const valueToRound = Math.abs(value) * 100;
        const scaled = shouldRoundDown
            ? Math.floor(valueToRound)
            : Math.ceil(valueToRound);

        if (scaled === 0) return '0';

        const integerPart = Math.floor(scaled / 100);
        const decimals = scaled % 100;

        const sign = isNegative ? '-' : '';

        if (decimals === 0) {
            return sign + integerPart;
        }

        const sep = this.translateService.getCurrentLang() === 'de' ? ',' : '.';

        return sign + integerPart + sep + decimals.toString().padStart(2, '0');
    }

    /**
     * Parses a formatted amount string (produced by truncateAndFormatAmount) back into a number.
     * Supports both 'en' (dot) and 'de' (comma) decimal separators.
     */
    private parseFormatted(formatted: string): number {
        return Number(formatted.replace(',', '.'));
    }

    /**
     * Master method that orchestrates width calculations for both sections using parsed numeric values.
     */
    private calculateWidths() {
        if (this.isUnlimited) {
            this.calculateUnlimitedWidths();
        } else {
            this.calculateBudgetDetailsWidths();
            this.calculateUsageDetailsWidths();
        }
    }

    /**
     * Calculates widths for unlimited subscriptions.
     *
     * Logic for unlimited subscriptions:
     * - Budget details (inclusive/extra) are hidden (not relevant for unlimited)
     * - Total shows '∞'
     * - Available shows '∞' and always displays translated text
     * - Used and blocked are shown only if their values > 0
     * - If neither used nor blocked exist, available takes full width
     * - If at least one exists, available gets minimum width for '∞' + text,
     *   remaining space is distributed between used and blocked proportionally
     */
    private calculateUnlimitedWidths(): void {
        // Budget details section: hide inclusive/extra for unlimited
        this.showExtra = false;
        this.showExtraText = false;
        this.extraWidth = 0;
        this.showInclusive = false;
        this.showInclusiveText = false;
        this.inclusiveWidth = 0;

        // Usage details section with special unlimited logic
        const used = this.parseFormatted(this.formattedAmounts.used);
        const blocked = this.parseFormatted(this.formattedAmounts.blocked);

        this.showUsed = used > 0;
        this.showBlocked = blocked > 0;

        // Case 1: Neither used nor blocked - available takes full width
        if (!this.showUsed && !this.showBlocked) {
            this.availableWidth = 100;
            this.usedWidth = 0;
            this.blockedWidth = 0;
            this.showAvailableText = true;
            this.showUsedText = false;
            this.showBlockedText = false;
            return;
        }

        // Case 2: At least one of used or blocked exists
        // Calculate minimum width needed for available (∞ + translated text)
        const minAvailableWidth = this.calculateMinWidthForInfinityWithText();

        // Count how many containers need space
        const visibleUsageContainers =
            (this.showUsed ? 1 : 0) + (this.showBlocked ? 1 : 0);

        // Account for gaps between containers (1% per gap)
        const availableSpaceWidth = 100 - visibleUsageContainers;

        // Reserve minimum width for available
        const remainingWidthForUsage = availableSpaceWidth - minAvailableWidth;

        // Distribute remaining width between used and blocked proportionally
        if (this.showUsed && this.showBlocked) {
            // Both used and blocked exist
            const totalUsage = used + blocked;
            const usedRatio = used / totalUsage;
            const blockedRatio = blocked / totalUsage;

            let calculatedUsedWidth = usedRatio * remainingWidthForUsage;
            let calculatedBlockedWidth = blockedRatio * remainingWidthForUsage;

            // Ensure minimum widths for numeric values
            const minUsedWidth = this.calculateMinWidthForFormatted('used');
            const minBlockedWidth =
                this.calculateMinWidthForFormatted('blocked');

            // Adjust if below minimums
            if (calculatedUsedWidth < minUsedWidth) {
                calculatedUsedWidth = minUsedWidth;
                calculatedBlockedWidth = Math.max(
                    minBlockedWidth,
                    remainingWidthForUsage - minUsedWidth
                );
            } else if (calculatedBlockedWidth < minBlockedWidth) {
                calculatedBlockedWidth = minBlockedWidth;
                calculatedUsedWidth = Math.max(
                    minUsedWidth,
                    remainingWidthForUsage - minBlockedWidth
                );
            }

            this.usedWidth = calculatedUsedWidth;
            this.blockedWidth = calculatedBlockedWidth;
        } else if (this.showUsed) {
            // Only used exists
            const minUsedWidth = this.calculateMinWidthForFormatted('used');
            this.usedWidth = Math.max(minUsedWidth, remainingWidthForUsage);
            this.blockedWidth = 0;
        } else {
            // Only blocked exists
            const minBlockedWidth =
                this.calculateMinWidthForFormatted('blocked');
            this.blockedWidth = Math.max(
                minBlockedWidth,
                remainingWidthForUsage
            );
            this.usedWidth = 0;
        }

        // Check if there's space for text labels
        this.showUsedText = this.showUsed
            ? this.hasSpaceForTextKey(
                  'used',
                  this.usedWidth,
                  this.USED_TRANSLATION_KEY
              )
            : false;

        this.showBlockedText = this.showBlocked
            ? this.hasSpaceForTextKey(
                  'blocked',
                  this.blockedWidth,
                  this.BLOCKED_TRANSLATION_KEY
              )
            : false;

        // Available always shows text in unlimited mode (we reserved space for it)
        this.availableWidth = minAvailableWidth;
        this.showAvailableText = true;
    }

    /**
     * Calculates widths and text visibility for budget details section (inclusive/extra).
     *
     * Logic:
     * - If extra > 0: Shows both containers with proportional widths
     * - If extra = 0: Hides extra container, inclusive takes full width
     * - Enforces minimum widths to ensure numeric values are always readable
     * - Hides text labels when containers are too narrow
     */
    private calculateBudgetDetailsWidths(): void {
        const inclusive = this.parseFormatted(this.formattedAmounts.inclusive);
        const extra = this.parseFormatted(this.formattedAmounts.extra);

        if (extra > 0) {
            this.showExtra = true;
            const totalAmount = inclusive + extra;

            const inclusiveRatio =
                totalAmount > 0 ? inclusive / totalAmount : 0;
            const extraRatio = totalAmount > 0 ? extra / totalAmount : 0;

            const availableWidth = 100 - 1; // 1% gap

            let calculatedInclusiveWidth = inclusiveRatio * availableWidth;
            let calculatedExtraWidth = extraRatio * availableWidth;

            const minInclusiveWidth =
                this.calculateMinWidthForFormatted('inclusive');
            const minExtraWidth = this.calculateMinWidthForFormatted('extra');

            let usingMinInclusiveWidth = false;
            let usingMinExtraWidth = false;

            if (calculatedInclusiveWidth < minInclusiveWidth) {
                calculatedInclusiveWidth = minInclusiveWidth;
                calculatedExtraWidth = Math.max(
                    minExtraWidth,
                    availableWidth - minInclusiveWidth
                );
                usingMinInclusiveWidth = true;
            } else if (calculatedExtraWidth < minExtraWidth) {
                calculatedExtraWidth = minExtraWidth;
                calculatedInclusiveWidth = Math.max(
                    minInclusiveWidth,
                    availableWidth - minExtraWidth
                );
                usingMinExtraWidth = true;
            }

            this.inclusiveWidth = calculatedInclusiveWidth;
            this.extraWidth = calculatedExtraWidth;

            this.showInclusiveText = usingMinInclusiveWidth
                ? false
                : this.hasSpaceForTextKey(
                      'inclusive',
                      calculatedInclusiveWidth,
                      this.INCLUSIVE_TRANSLATION_KEY
                  );

            this.showExtraText = usingMinExtraWidth
                ? false
                : this.hasSpaceForTextKey(
                      'extra',
                      calculatedExtraWidth,
                      this.EXTRA_TRANSLATION_KEY
                  );
        } else {
            this.showExtra = false;
            this.showExtraText = false;
            this.extraWidth = 0;
            this.inclusiveWidth = 100;
            this.showInclusiveText = this.hasSpaceForTextKey(
                'inclusive',
                100,
                this.INCLUSIVE_TRANSLATION_KEY
            );
        }
    }

    /**
     * Calculates widths and text visibility for usage details section (used/blocked/available).
     *
     * Logic:
     * - used/blocked containers only show if their values > 0
     * - available container is always shown (even if negative or zero)
     * - Uses absolute values for proportional width calculations
     * - Handles edge cases: only available visible, all values zero
     * - Enforces minimum widths and redistributes space proportionally
     * - Hides text labels when containers become too narrow
     */
    private calculateUsageDetailsWidths(): void {
        const used = this.parseFormatted(this.formattedAmounts.used);
        const blocked = this.parseFormatted(this.formattedAmounts.blocked);
        const available = this.parseFormatted(this.formattedAmounts.available);

        this.showUsed = used > 0;
        this.showBlocked = blocked > 0;

        const visibleContainers: Array<{
            name: 'used' | 'blocked' | 'available';
            value: number;
            absValue: number;
        }> = [];

        if (this.showUsed) {
            visibleContainers.push({
                name: 'used',
                value: used,
                absValue: Math.abs(used),
            });
        }
        if (this.showBlocked) {
            visibleContainers.push({
                name: 'blocked',
                value: blocked,
                absValue: Math.abs(blocked),
            });
        }
        visibleContainers.push({
            name: 'available',
            value: available,
            absValue: Math.abs(available),
        });

        if (visibleContainers.length === 1) {
            this.availableWidth = 100;
            this.usedWidth = 0;
            this.blockedWidth = 0;
            this.showAvailableText = true;
            return;
        }

        const totalAbsValue = visibleContainers.reduce(
            (sum, c) => sum + c.absValue,
            0
        );

        const availableSpaceWidth = 100 - (visibleContainers.length - 1);
        let calculatedUsedWidth = 0;
        let calculatedBlockedWidth = 0;
        let calculatedAvailableWidth = 0;

        for (const container of visibleContainers) {
            const ratio =
                totalAbsValue > 0 ? container.absValue / totalAbsValue : 0;
            const width = ratio * availableSpaceWidth;

            if (container.name === 'used') {
                calculatedUsedWidth = width;
            } else if (container.name === 'blocked') {
                calculatedBlockedWidth = width;
            } else {
                calculatedAvailableWidth = width;
            }
        }

        const minUsedWidth = this.showUsed
            ? this.calculateMinWidthForFormatted('used')
            : 0;
        const minBlockedWidth = this.showBlocked
            ? this.calculateMinWidthForFormatted('blocked')
            : 0;
        const minAvailableWidth =
            this.calculateMinWidthForFormatted('available');

        const adjusted = this.adjustUsageWidthsForMinimums(
            {
                used: calculatedUsedWidth,
                blocked: calculatedBlockedWidth,
                available: calculatedAvailableWidth,
            },
            {
                used: minUsedWidth,
                blocked: minBlockedWidth,
                available: minAvailableWidth,
            },
            availableSpaceWidth
        );

        this.usedWidth = adjusted.used;
        this.blockedWidth = adjusted.blocked;
        this.availableWidth = adjusted.available;

        this.showUsedText = this.showUsed
            ? this.hasSpaceForTextKey(
                  'used',
                  this.usedWidth,
                  this.USED_TRANSLATION_KEY
              )
            : false;

        this.showBlockedText = this.showBlocked
            ? this.hasSpaceForTextKey(
                  'blocked',
                  this.blockedWidth,
                  this.BLOCKED_TRANSLATION_KEY
              )
            : false;

        this.showAvailableText = this.hasSpaceForTextKey(
            'available',
            this.availableWidth,
            this.AVAILABLE_TRANSLATION_KEY
        );
    }

    /**
     * Adjusts container widths to ensure minimum width requirements are met.
     *
     * Process:
     * 1. Identifies containers that need minimum width enforcement
     * 2. Applies minimum widths where needed
     * 3. Calculates remaining space after applying minimums
     * 4. Redistributes remaining space proportionally among non-minimum containers
     *
     * This ensures numeric values are always readable while maintaining
     * proportional relationships where possible.
     *
     * @param calculatedWidths - Original proportional widths calculated from values
     * @param minWidths - Minimum required widths to display numeric values
     * @param totalAvailableWidth - Total width available for distribution (accounts for gaps)
     * @returns Adjusted widths that respect both proportions and minimum requirements
     */
    private adjustUsageWidthsForMinimums(
        calculatedWidths: { used: number; blocked: number; available: number },
        minWidths: { used: number; blocked: number; available: number },
        totalAvailableWidth: number
    ): { used: number; blocked: number; available: number } {
        let adjustedWidths = { ...calculatedWidths };

        const usedNeedsMin =
            this.showUsed && adjustedWidths.used < minWidths.used;
        const blockedNeedsMin =
            this.showBlocked && adjustedWidths.blocked < minWidths.blocked;
        const availableNeedsMin =
            adjustedWidths.available < minWidths.available;

        if (!usedNeedsMin && !blockedNeedsMin && !availableNeedsMin) {
            return adjustedWidths;
        }

        if (usedNeedsMin) {
            adjustedWidths.used = minWidths.used;
        }

        if (blockedNeedsMin) {
            adjustedWidths.blocked = minWidths.blocked;
        }

        if (availableNeedsMin) {
            adjustedWidths.available = minWidths.available;
        }

        const reservedForUsed = usedNeedsMin ? minWidths.used : 0;
        const reservedForBlocked = blockedNeedsMin ? minWidths.blocked : 0;
        const reservedForAvailable = availableNeedsMin
            ? minWidths.available
            : 0;

        const totalReservedWidth =
            reservedForUsed + reservedForBlocked + reservedForAvailable;

        const remainingWidth = Math.max(
            0,
            totalAvailableWidth - totalReservedWidth
        );

        const nonMinContainers = [] as string[];
        if (!usedNeedsMin && this.showUsed) {
            nonMinContainers.push('used');
        }

        if (!blockedNeedsMin && this.showBlocked) {
            nonMinContainers.push('blocked');
        }

        if (!availableNeedsMin) {
            nonMinContainers.push('available');
        }

        if (nonMinContainers.length > 0 && remainingWidth > 0) {
            let totalOriginalWidth = 0;
            if (nonMinContainers.includes('used')) {
                totalOriginalWidth += calculatedWidths.used;
            }

            if (nonMinContainers.includes('blocked')) {
                totalOriginalWidth += calculatedWidths.blocked;
            }

            if (nonMinContainers.includes('available')) {
                totalOriginalWidth += calculatedWidths.available;
            }

            if (totalOriginalWidth > 0) {
                if (nonMinContainers.includes('used')) {
                    const proportion =
                        calculatedWidths.used / totalOriginalWidth;
                    adjustedWidths.used = Math.max(
                        minWidths.used,
                        proportion * remainingWidth
                    );
                }
                if (nonMinContainers.includes('blocked')) {
                    const proportion =
                        calculatedWidths.blocked / totalOriginalWidth;
                    adjustedWidths.blocked = Math.max(
                        minWidths.blocked,
                        proportion * remainingWidth
                    );
                }
                if (nonMinContainers.includes('available')) {
                    const proportion =
                        calculatedWidths.available / totalOriginalWidth;
                    adjustedWidths.available = Math.max(
                        minWidths.available,
                        proportion * remainingWidth
                    );
                }
            } else {
                const equalWidth = remainingWidth / nonMinContainers.length;

                if (nonMinContainers.includes('used')) {
                    adjustedWidths.used = Math.max(minWidths.used, equalWidth);
                }

                if (nonMinContainers.includes('blocked')) {
                    adjustedWidths.blocked = Math.max(
                        minWidths.blocked,
                        equalWidth
                    );
                }

                if (nonMinContainers.includes('available')) {
                    adjustedWidths.available = Math.max(
                        minWidths.available,
                        equalWidth
                    );
                }
            }
        }

        return adjustedWidths;
    }

    /**
     * Calculates the minimum percentage width required to display the infinity symbol ('∞')
     * with translated "available" text for unlimited subscriptions.
     * @returns Width percentage (0–100) relative to the wrapper or a safe default if measurement is not possible
     */
    private calculateMinWidthForInfinityWithText(): number {
        const containerWidthPx =
            this.creditsViewWrapper?.nativeElement?.offsetWidth;
        if (!containerWidthPx || containerWidthPx === 0) return 10; // Safe default

        const infinityWidthPx = this.measureTextWidth(
            this.INFINITY_CHARACTER,
            this.AMOUNT_FONT
        );
        const translatedText = this.translateService.instant(
            this.AVAILABLE_TRANSLATION_KEY
        );
        const labelWidthPx = this.measureTextWidth(
            translatedText,
            this.LABEL_FONT
        );

        const totalNeededPx =
            labelWidthPx +
            this.LABEL_AMOUNT_GAP +
            infinityWidthPx +
            2 * this.CONTAINER_HORIZONTAL_PADDING;

        return (totalNeededPx / containerWidthPx) * 100;
    }

    /**
     * Calculates the minimum percentage width required to display the numeric amount string for the given key.
     * Measurement uses a canvas for accurate text width and includes horizontal padding. Returns 0 if wrapper width is not available.
     * @param key One of the formattedAmounts keys (inclusive|extra|used|blocked|available|total)
     * @returns Width percentage (0–100) relative to the wrapper or 0 if measurement is not possible
     */
    private calculateMinWidthForFormatted(key: keyof FormattedAmounts): number {
        const containerWidthPx =
            this.creditsViewWrapper?.nativeElement?.offsetWidth;
        if (
            !containerWidthPx ||
            containerWidthPx === 0 ||
            !this.formattedAmounts
        )
            return 0;

        const displayText = this.formattedAmounts[key];
        const textWidthPx = this.measureTextWidth(
            displayText,
            this.AMOUNT_FONT
        );
        const totalWidthPx = Math.max(
            60,
            textWidthPx + 2 * this.CONTAINER_HORIZONTAL_PADDING
        );
        return (totalWidthPx / containerWidthPx) * 100;
    }

    private hasSpaceForTextKey(
        key: keyof FormattedAmounts,
        widthPercent: number,
        translationKey: string
    ): boolean {
        const containerWidthPx =
            this.creditsViewWrapper?.nativeElement?.offsetWidth;
        if (!containerWidthPx || !this.formattedAmounts) return true;

        const amountWidthPx = this.measureTextWidth(
            this.formattedAmounts[key],
            this.AMOUNT_FONT
        );
        const translatedText = this.translateService.instant(translationKey);
        const labelWidthPx = this.measureTextWidth(
            translatedText,
            this.LABEL_FONT
        );
        const totalNeededPx =
            amountWidthPx +
            labelWidthPx +
            2 * this.CONTAINER_HORIZONTAL_PADDING +
            this.LABEL_AMOUNT_GAP;
        const availableWidthPx = (widthPercent / 100) * containerWidthPx;
        return availableWidthPx >= totalNeededPx;
    }

    /**
     * Measures the pixel width of text using Canvas API for accurate text measurement.
     *
     * Creates a temporary canvas element to measure text width with the specified font.
     * This provides more accurate measurements than CSS-based approaches and is used
     * for calculating minimum container widths and text visibility decisions.
     *
     * @param text - The text string to measure
     * @param font - CSS font specification (e.g., '20px Manrope-Regular, sans-serif')
     * @returns Width of the text in pixels
     */
    private measureTextWidth(text: string, font: string): number {
        // Create a canvas element for accurate text measurement
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        context.font = font;
        const metrics = context.measureText(text);

        return metrics.width;
    }

    protected getAvailabilityText() {
        const availabilityBasicKey = 'user.userArea.credits.availabilityText.';
        const suffix = this.isUnlimited ? 'unlimited' : 'general';
        return availabilityBasicKey + suffix;
    }
}
